/**
 * useSyncRealtime
 *
 * Subscribes to Supabase Realtime on the `change_events` table for
 * the current workspace. Provides live counts of conflicted and
 * quarantined events without requiring a full page reload.
 *
 * Usage:
 *   const { conflictCount, quarantineCount, lastEventAt } = useSyncRealtime();
 *
 * The hook:
 *   1. Fetches initial counts on mount.
 *   2. Subscribes to INSERT events on change_events for the workspace.
 *   3. Updates counts in real time as new events arrive.
 *   4. Cleans up the Realtime subscription on unmount.
 *
 * The counts update when:
 *   - A new inbound event arrives with sync_status='conflicted'.
 *   - The /dispatch function marks an event as conflicted.
 *   - A user resolves or dismisses a conflict (via triggerDispatch refresh).
 *
 * Note: Supabase Realtime delivers INSERT events immediately.
 *   UPDATE events (e.g. marking a conflict as synced) require subscribing
 *   to UPDATE as well — this hook also handles those to decrement counts.
 *
 * Phase 3 feature. Requires Supabase Realtime to be enabled on the
 * change_events table (database → replication in Supabase dashboard).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../lib/supabaseSync';

export interface SyncRealtimeState {
  /** Number of change_events with sync_status='conflicted' for this workspace. */
  conflictCount: number;
  /** Number of change_events with sync_status='quarantined' for this workspace. */
  quarantineCount: number;
  /** Number of change_events with sync_status='pending' for this workspace. */
  pendingCount: number;
  /** ISO timestamp of the most recently received change_event. */
  lastEventAt: string | null;
  /** Whether the Realtime subscription is connected. */
  isConnected: boolean;
  /** Error if the initial fetch or subscription failed. */
  error: string | null;
  /** Manually refresh counts from the database. */
  refresh: () => Promise<void>;
}

const EMPTY_STATE: Omit<SyncRealtimeState, 'refresh'> = {
  conflictCount: 0,
  quarantineCount: 0,
  pendingCount: 0,
  lastEventAt: null,
  isConnected: false,
  error: null,
};

export function useSyncRealtime(): SyncRealtimeState {
  const workspaceId = getCurrentWorkspaceId();
  const [state, setState] = useState<Omit<SyncRealtimeState, 'refresh'>>(EMPTY_STATE);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!workspaceId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('change_events')
        .select('sync_status')
        .eq('workspace_id', workspaceId)
        .in('sync_status', ['conflicted', 'quarantined', 'pending']);

      if (error) {
        setState((prev) => ({ ...prev, error: error.message }));
        return;
      }

      let conflictCount = 0;
      let quarantineCount = 0;
      let pendingCount = 0;

      for (const row of data ?? []) {
        const s = (row as { sync_status: string }).sync_status;
        if (s === 'conflicted') conflictCount++;
        else if (s === 'quarantined') quarantineCount++;
        else if (s === 'pending') pendingCount++;
      }

      setState((prev) => ({
        ...prev,
        conflictCount,
        quarantineCount,
        pendingCount,
        error: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to fetch sync counts',
      }));
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    // Initial fetch
    fetchCounts();

    // Subscribe to Realtime on change_events for this workspace
    const channelName = `sync-events-${workspaceId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'change_events',
          filter: `workspace_id=eq.${workspaceId}`,
        },
        (payload) => {
          const newRecord = payload.new as {
            sync_status: string;
            created_at: string;
            source: string;
          };

          setState((prev) => {
            const updates: Partial<typeof EMPTY_STATE> = {
              lastEventAt: newRecord.created_at,
            };

            if (newRecord.sync_status === 'conflicted') {
              updates.conflictCount = prev.conflictCount + 1;
            } else if (newRecord.sync_status === 'quarantined') {
              updates.quarantineCount = prev.quarantineCount + 1;
            } else if (newRecord.sync_status === 'pending') {
              updates.pendingCount = prev.pendingCount + 1;
            }

            return { ...prev, ...updates };
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'change_events',
          filter: `workspace_id=eq.${workspaceId}`,
        },
        (_payload) => {
          // On any update (e.g. conflict resolved → synced), refresh full counts
          // to keep numbers accurate without complex client-side diffing.
          fetchCounts();
        }
      )
      .subscribe((status) => {
        setState((prev) => ({
          ...prev,
          isConnected: status === 'SUBSCRIBED',
          error: status === 'CHANNEL_ERROR' ? 'Realtime connection error' : prev.error,
        }));
      });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [workspaceId, fetchCounts]);

  return {
    ...state,
    refresh: fetchCounts,
  };
}

/**
 * Returns just the total number of items needing attention
 * (conflicts + quarantined). Useful for badge counts.
 */
export function useSyncConflictBadgeCount(): number {
  const { conflictCount, quarantineCount } = useSyncRealtime();
  return conflictCount + quarantineCount;
}
